type CommonRoute = keyof typeof commonRoutes

const projectBase = '/project/'
export const projectBased = (subRoute: string) =>
  projectBase + ':projectCode' + subRoute

export function getProjectUrl(projectCode: string): string {
  return location.origin + getTransitionPath(projectBased(''), projectCode)
}

export const bareRoutes = {
  HOME: '/home',
  FORMS: '/forms',
  PROJECT_SETUP: '/projectSetup',
  DELIVERIES: '/deliveries',
  SHARED_DELIVERIES: '/shared-deliveries',
  SHARED_FORMS: '/shared-forms',
  NOTIFICATIONS: '/notifications',
  NOTIFICATION_SETTINGS: '/notification-settings',
  DOCUMENTS: '/documents',
  RESET_PASSWORD: '/reset-password',
  USERS_DIRECTORY: '/users',
  SIGN_UP: '/sign-up',
  QR_CODES: '/qr-codes',
  EQUIPMENT: '/equipment',
  STRUXTAGS: '/struxtags',
  LOCATIONS: '/locations',
  MATERIAL_RESOURCES: '/material-resources',
}

const commonRoutes = {
  LOGIN: '/login',
  SSO_CALLBACK: '/sso-callback',
  SSO_ADMIN_CONSENT: '/sso-admin-consent',
  SAVE_PASSWORD: '/save-password',
  RESET_PASSWORD: `${bareRoutes.RESET_PASSWORD}/:resetCode?`,

  INFO: '/info/:code?/:payload?',

  /**
   * @deprecated Mobile should use `SELECT_FORM_TYPE` instead.
   */
  FORM_TYPES: '/form-types',
  PROJECTS: '/projects',
  PORTAL: '/portal',
  PORTAL_PROFILE: '/portal/profile',
  REDIRECTOR: '/redirector',

  SIGN_UP: projectBased(bareRoutes.SIGN_UP),

  HOME: projectBased(bareRoutes.HOME),
  FORMS: projectBased(bareRoutes.FORMS),
  DELIVERIES: projectBased(bareRoutes.DELIVERIES),
  NOTIFICATIONS: projectBased(bareRoutes.NOTIFICATIONS),

  DELIVERY_DETAILS: projectBased(`${bareRoutes.DELIVERIES}/:deliveryId?`),
  SHARED_DELIVERY_DETAILS: projectBased(
    `${bareRoutes.SHARED_DELIVERIES}/:entityId?`,
  ),
  SHARED_FORM_DETAILS: projectBased(`${bareRoutes.SHARED_FORMS}/:entityId?`),
}

export function isCommonRoute(route): route is CommonRoute {
  return Object.keys(commonRoutes).includes(route)
}

export enum UrlParamKey {
  PasswordResetCode = 'oobCode',
  InviteKey = 'inviteKey',
  ForceInviteKey = 'forceInviteKey',
  /** Single-use session handoff code from another StruxHub origin (STX1-495) */
  HandoffCode = 'handoffCode',
  ReturnTo = 'return_to',
  AuthState = 'state',
  OverrideNavBar = 'overrideNavBar',
  DeliveryId = 'deliveryId',
  Form = 'form',
  /** Workflow type slug used in QR code links to pre-select form creation */
  FormType = 'formType',
  /** Legacy MN WebView access token: stripped from the URL, never honoured (STX1-398) */
  StxToken = 'stxToken',
  /** Tenant hostname passed from mobile WebView to ensure correct tenant resolution */
  TenantHost = 'tenantHost',
}

export const getURLParam = (path: string = location.pathname) =>
  path.split('/').slice(-1)[0]

// Public path — QR-code signup links land here without a session and
// must not bounce to login. All three bundles' TRY_UNAUTHORIZED_MODE
// handlers (desktop, mobile, login) flip isUnauthorizedMode = true on
// hit so the shared TRY_UNAUTHORIZED_MODE_RESULT handler skips the
// displayLoginView() bounce. WrappedRoute also bypasses the
// authenticated-user project-access check for signup paths.
export function isSignUpPageRequested(path: string = location.pathname) {
  return path.includes(bareRoutes.SIGN_UP)
}

// SSO callback and the tenantless admin-consent landing render their own
// result and manage auth state themselves, so startup auth-init must SKIP
// them instead of bouncing to /login. Kept as one predicate so the
// login-bundle and shared auth-entry handlers can't drift apart.
export function isSelfHandledAuthEntryPath(
  path: string = location.pathname,
): boolean {
  return (
    path === commonRoutes.SSO_CALLBACK ||
    path === commonRoutes.SSO_ADMIN_CONSENT
  )
}

export function isPathToDetails(path: string): boolean {
  const chunkCount = path.split('/').filter(c => !!c).length

  if (isProjectBasedPath(path)) {
    return chunkCount > 3
  }

  return chunkCount > 1
}

export const isDeliveryInfoPageRequested = (
  path: string = location.pathname,
): boolean => {
  return path.includes(bareRoutes.DELIVERIES) && isPathToDetails(path)
}

export function getTransitionPath(
  route: string = location.pathname,
  projectCode: string,
): string {
  if (isProjectBasedPath(route)) {
    return route.replace(':projectCode', projectCode)
  }

  return route
}

export function appendLocationSearchAndHash(
  path: string,
  search: string = location.search,
  hash: string = location.hash,
): string {
  if (!search && !hash) {
    return path
  }

  const url = new URL(path, location.origin)
  const searchParams = new URLSearchParams(search)

  searchParams.forEach((value, key) => {
    url.searchParams.append(key, value)
  })

  if (hash) {
    url.hash = hash.startsWith('#') ? hash.slice(1) : hash
  }

  return `${url.pathname}${url.search}${url.hash}`
}

const PROJECT_CODE_PATTERN = /^[A-Za-z0-9-]{1,20}$/
const HOME_PATH_PATTERN = /^\/project\/[A-Za-z0-9-]{1,20}\/home\/?$/

export function isValidProjectCode(code: string): boolean {
  return PROJECT_CODE_PATTERN.test(code)
}

export const getInitProjectCodeFromPath = (
  path: string = location.pathname,
): string => {
  // Deep links from Graph are built as `{desktopUrl}/project/{code}/...` and most tenants
  // store desktopUrl with a trailing slash, so the path arrives as `//project/<code>/`.
  // Browsers keep duplicate path segments verbatim, so collapse the leading run before
  // matching -- and use the collapsed value for the split too, or index [2] reads
  // 'project' as the project code (STX1-1046).
  //
  // Deliberately leading-only: a trailing-slash base URL joined to a leading-slash path
  // can only ever double the FIRST separator. Collapsing interior runs as well would slide
  // the following segment into the code slot when the code segment is empty, so
  // `/project//home` would resolve to the view name `home` instead of ''.
  const normalized = path.replace(/^\/{2,}/, '/')
  if (isProjectBasedPath(normalized)) {
    const code = normalized.split('/')[2]
    if (PROJECT_CODE_PATTERN.test(code)) {
      return code
    }
  }

  return ''
}

export function isProjectBasedPath(path: string = location.pathname): boolean {
  return path.startsWith(projectBase)
}

export function isProjectHomePath(path: string = location.pathname): boolean {
  return HOME_PATH_PATTERN.test(path)
}

/**
 * A concrete project deep link names a destination beyond the bare project
 * root: `/project/<code>/<view>[/<detail>…]` → `['project', '<code>',
 * '<view>', …]`. We don't allow-list known views (the router renders the
 * matching view or falls back to the default route if it isn't real, matching
 * an authenticated direct visit), but we DO require a well-formed `<code>` so a
 * malformed `/project/<garbage>/…` falls to the configured default instead of
 * being chased into a dead route (STX1-540).
 */
export function isProjectDeepLink(path: string): boolean {
  if (!isProjectBasedPath(path)) {
    return false
  }
  const [, code, view] = path.split('/').filter(Boolean)
  return !!view && isValidProjectCode(code)
}

export function getJHPStandaloneUrl(projectCode: string): string | null {
  if (!isValidProjectCode(projectCode)) {
    return null
  }
  return `/jhp/${projectCode}`
}

/**
 * Determines if an unauthenticated user should be redirected from /project/{code}/home to JHP.
 * Returns the target URL if redirect should occur, null otherwise.
 * Skips redirect if ?login or ?inviteKey params are present.
 *
 * This is a pure function - callers are responsible for performing the actual navigation.
 */
export function getJHPRedirectUrl(
  pathname: string,
  projectCode: string,
  searchParams: URLSearchParams = new URLSearchParams(window.location.search),
): string | null {
  if (!isProjectHomePath(pathname) || !projectCode) {
    return null
  }

  if (
    searchParams.has('login') ||
    searchParams.has(UrlParamKey.InviteKey) ||
    searchParams.has(UrlParamKey.HandoffCode)
  ) {
    return null
  }

  return getJHPStandaloneUrl(projectCode)
}

// Build the cross-bundle device-version redirect URL. Carries the full
// destination — pathname + search + hash — so a deep link with a hash
// (e.g. `…/forms?form=<id>#details`) survives the bounce to the right
// bundle; otherwise the destination bundle never sees the hash and can't
// recover it into initDeepLink (STX1-582).
export function buildDeviceRedirectUrl(
  host: string,
  loc: Pick<Location, 'pathname' | 'search' | 'hash'> = location,
): string {
  return host + loc.pathname + loc.search + loc.hash
}

export const getRedirect = (
  { isDesktopVersion, redirects },
  isMobile: boolean,
  isTablet: boolean,
): {
  redirectRequired: boolean
  redirect: any
  toDesktop: boolean
  host: string
} => {
  let redirectRequired = false
  let host = location.origin
  let toDesktop = isDesktopVersion
  let redirect

  // check if redirect to correct device is needed
  if (
    !isTablet &&
    ((isDesktopVersion && isMobile) || (!isDesktopVersion && !isMobile))
  ) {
    const url = window.location.origin
    toDesktop = !isDesktopVersion
    redirect = redirects.find(r => url.includes(r.from))
    if (redirect) {
      redirectRequired = true
      host = host.replace(redirect.from, redirect.to)
    }
  }

  return { redirectRequired, redirect, toDesktop, host }
}

export default commonRoutes
